import { useQuery } from "@tanstack/react-query";
import { api, queryString } from "../../api/client";
import type {
  AutomationRule,
  EmailMessage,
  EmailTemplate,
  EmailThread,
  ListResponse,
  Mailbox,
} from "../../api/types";

// The mailbox lives under /api/v1/messaging because the same module owns the sender
// identity, the templates and the delivery log. Threads are the conversational view over
// those deliveries rather than a separate store, so there is still exactly one record of
// what was actually sent to a customer.

// venueId is the venue in the top bar. A conversation follows its customer, so the mailbox
// narrows the way the contacts list does; "" reads every venue the membership can see.
export function useEmailThreads(
  search: string,
  status: string,
  customerId: string,
  venueId: string,
) {
  return useQuery({
    queryKey: ["messaging", "threads", search, status, customerId, venueId],
    queryFn: () =>
      api<ListResponse<EmailThread>>(
        `/api/v1/messaging/threads${queryString({
          query: search,
          status,
          customerId,
          venueId,
          limit: 100,
          offset: 0,
        })}`,
      ),
  });
}

export function useEmailThread(threadId: string | undefined) {
  return useQuery({
    queryKey: ["messaging", "threads", "detail", threadId],
    queryFn: () => api<EmailThread>(`/api/v1/messaging/threads/${threadId}`),
    enabled: Boolean(threadId),
  });
}

// Delivery states that resolve on their own within seconds: the job processor moves a message from
// queued through sending to sent or failed, and nothing pushes that transition to the browser. The
// conversation therefore polls while any message is still in flight and stops the moment none is,
// rather than sitting on whatever it happened to fetch until somebody reloads the page.
//
// `scheduled` is deliberately not in this set. It is a resting state that can last days, so every
// open conversation holding a scheduled reply would poll forever for a transition that is not due.
const inFlightMessageStatuses = new Set(["queued", "sending"]);

export function useEmailThreadMessages(threadId: string | undefined) {
  return useQuery({
    queryKey: ["messaging", "threads", "detail", threadId, "messages"],
    queryFn: () =>
      api<ListResponse<EmailMessage>>(`/api/v1/messaging/threads/${threadId}/messages`),
    enabled: Boolean(threadId),
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((message) => inFlightMessageStatuses.has(message.status))
        ? 4000
        : false,
  });
}

// Named once and exported, so a screen that changes the wording refreshes exactly the read it
// changed rather than reaching for a string that has to match this one by luck.
export const emailTemplatesKey = ["messaging", "templates"] as const;

// Templates are read here with emails.send rather than integrations.manage: nobody can
// send from wording they are not allowed to see. Authoring that wording stays with the
// administrator, which is a different screen and a different permission.
export function useEmailTemplates() {
  return useQuery({
    queryKey: emailTemplatesKey,
    queryFn: () => api<ListResponse<EmailTemplate>>("/api/v1/messaging/templates"),
  });
}

export interface AutomationRuleList extends ListResponse<AutomationRule> {
  // The closed list of events a rule may react to travels with the rules, so the editor
  // cannot offer a trigger the dispatcher never fires on.
  triggerEvents: string[];
}

export function useAutomationRules() {
  return useQuery({
    queryKey: ["messaging", "automation-rules"],
    queryFn: () => api<AutomationRuleList>("/api/v1/messaging/automation-rules"),
  });
}

// The two states a conversation can be in. The database admits exactly these (threads_status_check),
// so the filter offers exactly these; the "waiting" and "resolved" options the mailbox used to
// show could never match a row.
export const threadStatusLabels: Record<string, string> = {
  open: "Open",
  closed: "Closed",
};

export const threadPurposeLabels: Record<string, string> = {
  service: "Service",
  marketing: "Marketing",
  inbound: "Inbound",
  outbound: "Outbound",
  general: "General",
};

// Delivery states staff actually see in a conversation. "Scheduled" is the one that can
// still be stopped, which is why the timeline treats it differently from everything else.
export const messageStatusLabels: Record<string, string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  queued: "Queued",
  sending: "Sending",
  sent: "Sent",
  delivered: "Delivered",
  deferred: "Deferred",
  failed: "Failed",
  bounced: "Bounced",
  complained: "Complaint",
  cancelled: "Canceled",
  received: "Received",
  quarantined: "Quarantined",
  rejected: "Rejected",
  opened: "Opened",
  clicked: "Clicked",
};

// The event names are the published contract, so they are shown as they are rather than
// paraphrased. A friendlier label per event would drift from what the rule actually
// matches the moment a new event is added.
export function triggerEventLabel(value: string): string {
  return value.replace(/\.v1$/u, "").replaceAll(".", " ").replaceAll("_", " ");
}

// A delay reads as a duration to the person setting it, not as a minute count. Zero is
// meaningfully different from any other value: it is the message that goes out with the
// event rather than after it.
export function delayLabel(minutes: number): string {
  if (minutes <= 0) return "Immediately";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} later`;
  if (minutes < 1440) {
    const hours = Math.round(minutes / 60);
    return `${hours} hour${hours === 1 ? "" : "s"} later`;
  }
  const days = Math.round(minutes / 1440);
  return `${days} day${days === 1 ? "" : "s"} later`;
}

// ---------------------------------------------------------------------------
// Mailbox hooks
// ---------------------------------------------------------------------------

export function useMailboxes() {
  return useQuery({
    queryKey: ["messaging", "mailboxes"],
    queryFn: () => api<ListResponse<Mailbox>>("/api/v1/messaging/mailboxes"),
  });
}

export function useMailbox(mailboxId: string | undefined) {
  return useQuery({
    queryKey: ["messaging", "mailboxes", "detail", mailboxId],
    queryFn: () => api<Mailbox>(`/api/v1/messaging/mailboxes/${mailboxId}`),
    enabled: Boolean(mailboxId),
  });
}

// ---------------------------------------------------------------------------
// Attachment helpers
// ---------------------------------------------------------------------------

export function attachmentDownloadUrl(threadId: string, attachmentId: string): string {
  return `/api/v1/messaging/threads/${threadId}/attachments/${attachmentId}`;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
